/**
 * Schritt „Aussehen“ (Punkt 134, docs/PLAN-WIDGET-AUSSEHEN.md): Akzent, Knopf-Text, Schrift, Ecken
 * je Widget, gespeichert in `draftConfig.aussehen`. Fuer Formular- UND Bewertungs-Widgets.
 * Kontrast wird nur gewarnt (nie gesperrt), mit Knopf „Lesbare Textfarbe übernehmen“.
 * Leere Werte werden weggelassen, nicht als null gespeichert.
 */
import { useEffect, useState } from "react";
import { ECKEN, istHexFarbe, kontrast, lesbareTextfarbe, type Aussehen } from "@maestro/widgets/aussehen";
import { Button, Card, Icon, Input, Select } from "../../../ui/components";
import type { WidgetEditor } from "../useWidgetEditorForm";

/** Vorgaben aus `kern.ts` `shadowStyles()`, wenn nichts gesetzt ist. */
export const VORGABE_AKZENT = "#1f2937";

const SCHRIFT_OPTIONEN = [
  { value: "website", label: "Wie deine Website (Vorgabe)" },
  { value: "serifenlos", label: "Serifenlos" },
  { value: "serif", label: "Klassisch mit Serifen" },
  { value: "elegant", label: "Elegant (Playfair, falls deine Website sie lädt)" },
];
const ECKEN_TEXT: Record<number, string> = { 0: "Eckig", 4: "Leicht", 8: "Rund", 16: "Sehr rund" };

/** Was auf dem Knopf tatsaechlich steht — dieselbe Regel wie der Loader (`dom.ts` `setzeAkzent`). */
export function knopfFarben(a: Aussehen): { akzent: string; text: string } {
  const akzent = a.akzent ?? VORGABE_AKZENT;
  return { akzent, text: a.knopfText ?? (a.akzent ? lesbareTextfarbe(a.akzent) ?? "#ffffff" : "#ffffff") };
}

const LABEL = "mb-1 block font-body-sm text-body-sm font-semibold text-on-surface dark:text-inverse-on-surface";
const HILFE = "mt-1 block font-label-md text-label-md text-on-surface-variant";

function FarbFeld({ titel, wert, platzhalter, onWert }: { titel: string; wert: string | undefined; platzhalter: string; onWert: (v: string | undefined) => void }) {
  // Halb getippte Farben bleiben lokal, gespeichert wird nur #RRGGBB oder leer (sonst lehnt der Server ab).
  const [text, setText] = useState(wert ?? "");
  useEffect(() => setText(wert ?? ""), [wert]);
  return (
    <label className="block">
      <span className={LABEL}>{titel}</span>
      <span className="flex items-center gap-2">
        <input
          type="color"
          aria-label={`${titel} wählen`}
          className="h-10 w-12 shrink-0 rounded-lg border border-outline-variant/40"
          value={wert ?? platzhalter}
          onChange={(e) => onWert(e.target.value)}
        />
        <Input
          type="text"
          maxLength={7}
          placeholder="Automatisch"
          className="font-mono"
          aria-label={titel}
          value={text}
          onChange={(e) => {
            const v = e.target.value.trim();
            setText(v);
            if (v === "" || istHexFarbe(v)) onWert(v || undefined);
          }}
        />
      </span>
    </label>
  );
}

export default function AussehenStep({ editor, onNext }: { editor: WidgetEditor; onNext: () => void }) {
  const a = editor.form.aussehen;
  const setze = <K extends keyof Aussehen>(k: K, v: Aussehen[K] | undefined) =>
    editor.updateForm((f) => {
      const aussehen = { ...f.aussehen };
      if (v === undefined || v === "") delete aussehen[k];
      else aussehen[k] = v;
      return { ...f, aussehen };
    });

  const farben = knopfFarben(a);
  const wert = kontrast(farben.akzent, farben.text);
  const lesbar = lesbareTextfarbe(farben.akzent);
  const stufe = wert === null ? null : wert >= 4.5 ? "gut" : wert >= 3 ? "knapp" : "schlecht";

  return (
    <Card className="space-y-5 p-4" data-testid="aussehen-schritt">
      <div className="grid gap-4 sm:grid-cols-2">
        <FarbFeld titel="Akzentfarbe" wert={a.akzent} platzhalter={VORGABE_AKZENT} onWert={(v) => setze("akzent", v)} />
        <FarbFeld titel="Textfarbe auf Knöpfen" wert={a.knopfText} platzhalter={farben.text} onWert={(v) => setze("knopfText", v)} />
      </div>
      <span className={HILFE}>Leer lassen heißt: Auf der Website wählt das Widget selbst. Format #RRGGBB.</span>

      {stufe && (
        <div
          role="status"
          data-testid="kontrast"
          data-stufe={stufe}
          className={`flex flex-wrap items-center gap-2 rounded-xl p-3 font-body-sm text-body-sm ${
            stufe === "gut" ? "bg-secondary/10 text-secondary dark:text-[#b8ae9e]" : stufe === "knapp" ? "bg-tertiary-container/60 text-on-tertiary-container" : "bg-error/10 text-error"
          }`}
        >
          <Icon name={stufe === "gut" ? "check_circle" : "warning"} className="text-[18px]" />
          <span>
            Kontrast {wert!.toFixed(1).replace(".", ",")} : 1 —{" "}
            {stufe === "gut" ? "gut lesbar." : stufe === "knapp" ? "knapp, für große Schrift reicht es." : "die Knopf-Schrift ist kaum lesbar."}
          </span>
          {stufe !== "gut" && lesbar && (
            <Button type="button" variant="secondary" onClick={() => setze("knopfText", lesbar)}>
              Lesbare Textfarbe übernehmen
            </Button>
          )}
        </div>
      )}

      <div>
        <span className={LABEL}>Schrift</span>
        <Select ariaLabel="Schrift" value={a.schrift ?? "website"} onValueChange={(v) => setze("schrift", v === "website" ? undefined : v)} options={SCHRIFT_OPTIONEN} />
        <span className={HILFE}>Es werden keine Schriften von fremden Servern nachgeladen.</span>
      </div>

      <div>
        <span className={LABEL}>Ecken</span>
        <div role="group" aria-label="Ecken" className="flex flex-wrap gap-2">
          {ECKEN.map((px) => (
            <button
              key={px}
              type="button"
              aria-pressed={a.ecken === px}
              className={`rounded-full border px-3 py-1 font-label-md text-label-md ${
                a.ecken === px ? "border-primary bg-primary/10 font-semibold text-primary" : "border-outline-variant/40 text-on-surface-variant"
              }`}
              onClick={() => setze("ecken", a.ecken === px ? undefined : px)}
            >
              {ECKEN_TEXT[px]}
            </button>
          ))}
        </div>
      </div>

      <p className="font-label-md text-label-md text-on-surface-variant">
        Hat deine Website einen eigenen Stil-Block für das Widget, gewinnt der. Änderungen erscheinen auf der Website nach dem Publizieren.
      </p>

      <div className="flex justify-end pt-2">
        <Button type="button" onClick={onNext}>
          Weiter <Icon name="arrow_forward" className="text-[16px]" />
        </Button>
      </div>
    </Card>
  );
}
